import { FC } from 'react';
import { IQueryParamDrivenDrawerContentProvider } from '../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { useRerenderOnUploadChange } from '../notes/generic-notes/attachments/useRerenderOnUploadChange';
import { QueryParamDrivenDrawerPage } from '../components/CareMapDrawerPage';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { IonButton } from '@ionic/react';
import { useSubscriptionV2 } from '../support/state/useSubscriptionV2';
import { ActiveCaremapNoteEditor } from '../notes/edit-note/ActiveCaremapNoteEditor';
import { AlertPresenter } from '../AlertBinder';
import { HistoryViewModel } from '../support/HistoryViewModel';

interface IEditCommunityPostDrawerProps {
    noteId: string;
};

export class EditCommunityPostWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IEditCommunityPostDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IEditCommunityPostDrawerProps>){}

    mode = 'edit-community-post';

    Component = EditNoteDrawerView;

    open = (props: IEditCommunityPostDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    close = () => {
        this.drawerHelper.close();
    };
};

export const EditNoteDrawerView: FC<IEditCommunityPostDrawerProps> = ({ noteId }) => {
    const alert = useInstance(AlertPresenter);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    useRerenderOnUploadChange();

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const history = useInstance(HistoryViewModel);

    const noteVM = useSubscriptionV2(async () => await notesFactory.createEditForm(noteId));

    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const saveAndClose = async () => {
        if (!noteVM){
            return;
        }
        try {
            await noteVM.save({ trackingEvent: 'Community Post Edited' });
            drawerHelper.close();
        }
        catch (err: any){
            alert.showAndLogError(err);
        }
    };

    if (!noteVM){
        return null;
    }

    const activeCareMap = careMapState.getActiveCaremap();
    if (!activeCareMap){
        return null;
    }

    let title = 'Edit Post';

    const canEdit = careMapState.hasPermissionTo('put-note');
    if (!canEdit){
        title = 'View Post';
    }

    return (
        <QueryParamDrivenDrawerPage
            title={title}
            hasBack={false}
            data-type="note-editor"
            onClose={!canEdit ? drawerHelper.close : undefined}
            buttons={canEdit && (
                <>
                    <IonButton
                        data-type="save-button"
                        disabled={!noteVM.canSave()}
                        onClick={saveAndClose}>Save</IonButton>
                    <IonButton data-type="cancel-button" fill="outline" onClick={() => {
                        history.back();
                    }}>Cancel</IonButton>
                </>
            )}
        >
            <ActiveCaremapNoteEditor
                excludeMenu
                noteVM={noteVM}
                includeTemplates
                onDeleted={drawerHelper.close}
                hasPrivateCheckbox={false}
            />
        </QueryParamDrivenDrawerPage>
    );
};

